import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import MaintenanceObjective from "@/common/components/MaintenanceObjective";
import { cn } from "@/common/utils/cn";
import { MaintenanceForwardingData } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";
import { useFormContext } from "react-hook-form";
import { UpdateDiagnosticEntryValidationSchema } from "../../../../../schemas/updateDiagnosticEntry.schema";
import DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectivePieces from "./DiagnosticObjectivePieces";
import DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectiveActivities from "./DiagnosticObjectiveActivities";
import { useMemo, useState } from "react";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { MaintenanceForwardingDirectiveEntryMap } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/directiveEntry.type";
import { ChevronDown, ChevronUp } from "lucide-react";

interface DiagnosticEntryUpdateObjectiveAttributesProps {
  forwarding: MaintenanceForwardingData<
    MaintenanceForwardingDirectiveEntryMap[MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry]
  > | undefined;
  submit: VoidFunction;
  dataUpdatedAt?: number;
}

const DiagnosticEntryUpdateObjectiveAttributes = ({
  forwarding,
  submit,
  dataUpdatedAt,
}: DiagnosticEntryUpdateObjectiveAttributesProps) => {
  const {
    setValue,
    formState: {
      disabled: formDisabled,
    },
  } = useFormContext<UpdateDiagnosticEntryValidationSchema>();

  const objectives = useMemo(() => {
    const objectives = forwarding?.directive_entry?.objectives;

    objectives?.sort((a, b) => a.id.localeCompare(b.id));

    return objectives;
  }, [dataUpdatedAt]);

  const [collapsedState, setCollapsedState] = useState<Record<string, boolean>>({});

  const toggleCollapsed = (id: string) =>
    setCollapsedState((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-lg font-medium">
          Objetos originais
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {objectives?.map((objective, index) => (
          <Box
            key={objective.id}
            className={cn(
              'flex flex-col gap-4 p-4 rounded-lg',
              objective.does_not_need_estimate === true && 'bg-danger-op',
            )}
          >
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 flex-wrap justify-between">
              <MaintenanceObjective
                systemDisplayName={objective.system.display_name}
                actionDisplayName={objective.action.display_name}
                partDisplayName={objective.part.display_name}
                actionDescription={objective.action.description}
              />

              <div className="flex gap-3 ml-auto">
                {objective.does_not_need_estimate && (
                  <Button variant="neutral" className="pointer-events-none">
                    Sem orçamento
                  </Button>
                )}

                {!objective.does_not_need_estimate && (
                  <Button
                    variant="outline-error"
                    disabled={formDisabled}
                    onClick={() => {
                      setValue(`objectives_attributes.${index}.does_not_need_estimate`, true);

                      submit();
                    }}
                  >
                    Nada a ser orçado
                  </Button>
                )}

                {objective.does_not_need_estimate !== false && (
                  <Button
                    variant="outline-success"
                    disabled={formDisabled}
                    onClick={() => {
                      setValue(`objectives_attributes.${index}.does_not_need_estimate`, false);

                      submit();
                    }}
                  >
                    Orçar
                  </Button>
                )}

                {objective.does_not_need_estimate === false && <Button variant="link-white" onClick={() => toggleCollapsed(objective.id)}>
                  {collapsedState[objective.id]
                    ? <ChevronDown strokeWidth={1} className="text-white" />
                    : <ChevronUp strokeWidth={1} className="text-white" />
                  }
                </Button>}
              </div>
            </div>

            {!collapsedState[objective.id] && objective.does_not_need_estimate === false && (
              <div className="flex flex-col gap-4">
                <DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectivePieces
                  diagnosticObjective={objective}
                />

                <DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectiveActivities
                  diagnosticObjective={objective}
                />
              </div>
            )}
          </Box>
        ))}
      </div>
    </div>
  );
}

export default DiagnosticEntryUpdateObjectiveAttributes;
